
import {
  sendOfficeFollowUp,
  updateInvoiceNotes,
  getProgressPayments,
  updateInvoice,
  sendHygAsstResponse,
  claimPayment,
  confirmManualPayment,
  retryAutomaticPayout,
  nudgeJames,
  nudgeSent,
  updateTable
} from "@/api";
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import ProgressPayments from "./components/ProgressPayments.vue";
import Resolves from "./components/Resolves.vue";
import { format, zonedTimeToUtc } from "date-fns-tz";
import { currentTimeZone } from "@/helpers";
import { getOfficeChargeFailureMessage } from "@/helpers/claimPaymentOutcome";
import {
  canConfirmManualTalentPayment,
  canRetryAutomaticPayout,
  filterPaymentIntegrityActions,
  runBoundAutomaticPayoutRetryAction,
  runBoundManualTalentPaymentAction,
  runBoundOfficeChargeAction
} from "@/helpers/paymentLifecycleAdminGuard";

export default Vue.extend({
  name: "SelectedRecord",
  components: { ProgressPayments, Resolves },
  data: () => {
    return {
      dialog: false,
      dialogNotes: false,
      action_dialog: false,
      progressPayments_dialog: false,
      retryPayout_dialog: false,
      action_dialog_title: "",
      errors: false,
      message: "",
      note: "",
      modalNotes: "",
      modalNotesTitle: "",
      progressPayments: null as any,
      paymentActionPending: false,
      timeZone: currentTimeZone(),
      actions: [
        "Send Office Follow-Up",
        "Send Hyg/Asst Response",
        "Create Note",
        "Process Payment",
        "Confirm Manual Talent Payment",
        "Resolved",
        "Add Ticket Link"
      ],
      selected: ""
    };
  },
  created() {
    this.payInts[0].payNowUsed && this.actions.push("Nudge James", "Sent");
  },
  computed: {
    ...mapGetters({
      payInts: "getSinglePaymentIntegrity"
    }),
    availableActions(): string[] {
      return filterPaymentIntegrityActions(this.actions, this.payInts[0]);
    },
    showAutomaticPayoutRetry(): boolean {
      return canRetryAutomaticPayout(this.payInts[0]);
    }
  },
  methods: {
    ...mapActions(["getPaymentIntegrities", "addAdminLogging"]),
    formatDate(date: string | undefined): string | null {
      if (!date) return null;
      return format(zonedTimeToUtc(date, this.timeZone), "MMMM do yyyy");
    },
    formatNotes(notes: string): string | null {
      return notes.length > 0 ? notes.replace(/\|/gi, "<br />") : null;
    },
    openDialog(msg: string, errors = false): void {
      this.updateTable();
      this.message = msg;
      this.errors = errors;
      this.dialog = true;
      this.action_dialog = false;
    },
    async updateTable(): Promise<void> {
      await this.getPaymentIntegrities({
        column: "i.id",
        value: this.payInts[0].id,
        limit: 1
      });
    },
    reset(): void {
      setTimeout(() => {
        this.$emit("reset");
      }, 2000);
    },
    actionDialog(title) {
      this.action_dialog = true;
      this.action_dialog_title = title;
      this.note = "";
    },
    async submitSelect() {
      if (this.paymentActionPending) return;
      switch (this.selected) {
        case "Send Office Follow-Up":
          this.sendOffice();
          this.addLogging("Send Office Follow-Up");
          break;
        case "Send Hyg/Asst Response":
          this.sendHygAsstResponse();
          this.addLogging("Send Hyg/Asst Response");
          break;
        case "Create Note":
          this.actionDialog("Enter Payment Note");
          break;
        case "Process Payment":
          this.getProgressPayments();
          this.addLogging("Process Payment");
          break;
        case "Confirm Manual Talent Payment":
          if (!canConfirmManualTalentPayment(this.payInts[0])) break;
          this.actionDialog("Confirm Manual Talent Payment");
          this.addLogging("Confirm Manual Talent Payment");
          break;
        case "Resolved":
          this.actionDialog("How was payment resolved?");
          this.addLogging("Resolved");
          break;
        case "Add Ticket Link":
          this.actionDialog("Add Ticket Link");
          break;
        case "Nudge James":
          await nudgeJames(this.payInts[0].id);
          this.addLogging("Nudge James");
          this.openDialog("Nudge James - sent");
          break;
        case "Sent":
          await nudgeSent(this.payInts[0].id);
          this.addLogging("Sent");
          this.openDialog("Sent - sent");
          this.reset();
          break;
        default:
          console.log("Please, select option");
          break;
      }
      this.selected = "";
    },
    async sendOffice(): Promise<void> {
      try {
        await sendOfficeFollowUp(this.payInts[0]);
        this.openDialog("Office Follow-Up Sent");
      } catch (error) {
        this.openDialog("Office Follow-Up didn't send", true);
      }
    },
    async updateNotes(): Promise<void> {
      try {
        await updateInvoiceNotes({
          invoiceID: this.payInts[0].id,
          note: this.note
        });
        await this.updateTable(); // Refresh the data to show the updated notes
        this.openDialog("The notes were updated");
      } catch (error) {
        this.openDialog("The notes weren't update", true);
      }
    },
    async getProgressPayments(): Promise<void> {
      try {
        const data = await getProgressPayments(this.payInts[0].id);
        this.progressPayments = data[0];
        this.progressPayments_dialog = true;
      } catch (error) {
        this.openDialog("The progress payments not found", true);
      }
    },
    async sendProgressPayments(): Promise<void> {
      if (this.paymentActionPending || !this.payInts[0]?.id) return;
      const invoiceId = this.payInts[0].id;
      this.paymentActionPending = true;
      this.progressPayments_dialog = false;
      const dateNow = format(new Date(), "yyyy-MM-dd HH:mm:ss");

      try {
        const outcome = await runBoundOfficeChargeAction(invoiceId, dateNow, {
          claimPayment,
          writeNote: (targetInvoiceId, note) =>
            updateInvoiceNotes({ invoiceID: targetInvoiceId, note }),
          updateInvoice,
          nudgeJames
        });
        if (this.payInts[0]?.id === invoiceId) {
          this.openDialog(outcome.message, outcome.requiresAction);
          this.payInts[0].notes += dateNow + " " + outcome.note;
          if (!outcome.requiresAction) this.reset();
        }
      } catch (error) {
        const warning = getOfficeChargeFailureMessage(error);
        try {
          await updateInvoiceNotes({ invoiceID: invoiceId, note: warning });
        } catch (_) {
          // Always show the warning, even when the audit note cannot be saved.
        }
        if (this.payInts[0]?.id === invoiceId) {
          this.openDialog(warning, true);
        }
      } finally {
        this.paymentActionPending = false;
      }
    },
    async updateResolved(): Promise<void> {
      try {
        const dateNow = format(new Date(), "yyyy-MM-dd HH:mm:ss");

        Promise.all([
          updateInvoiceNotes({
            invoiceID: this.payInts[0].id,
            note: `resolved by ${this.note}`
          }),
          updateInvoice({
            invoiceId: this.payInts[0].id,
            resolved: dateNow
          })
        ]);
        this.openDialog("The resolve was updated");
        this.payInts[0].notes += dateNow + ` resolved by ${this.note}`;
        this.reset();
      } catch (error) {
        this.openDialog("The resolve wasn't updated", true);
      }
    },
    async confirmManualTalentPayment(): Promise<void> {
      if (this.paymentActionPending || !this.payInts[0]?.id) return;
      const invoiceId = this.payInts[0].id;
      const reference = this.note.trim();
      if (!reference || !canConfirmManualTalentPayment(this.payInts[0])) return;
      this.paymentActionPending = true;
      try {
        const dateNow = format(new Date(), "yyyy-MM-dd HH:mm:ss");
        const outcome = await runBoundManualTalentPaymentAction(
          invoiceId,
          reference,
          dateNow,
          {
            confirmManualPayment,
            writeNote: (targetInvoiceId, note) =>
              updateInvoiceNotes({ invoiceID: targetInvoiceId, note }),
            updateInvoice
          }
        );
        if (this.payInts[0]?.id === invoiceId) {
          this.openDialog(outcome.message, outcome.requiresAction);
          // reset() destroys this dialog after two seconds. Keep a financial
          // warning visible until the operator acknowledges it.
          if (!outcome.requiresAction) this.reset();
        }
      } catch (error) {
        if (this.payInts[0]?.id === invoiceId) {
          this.openDialog(
            "Manual Talent payment outcome could not be confirmed. Do not pay again or send a replacement payment; refresh and contact Support.",
            true
          );
        }
      } finally {
        this.paymentActionPending = false;
      }
    },
    async submitAutomaticPayoutRetry(): Promise<void> {
      if (this.paymentActionPending || !this.payInts[0]?.id) return;
      const invoiceId = this.payInts[0].id;
      this.retryPayout_dialog = false;
      if (!canRetryAutomaticPayout(this.payInts[0])) return;
      this.paymentActionPending = true;
      this.addLogging("Retry Automatic Payout");
      try {
        const outcome = await runBoundAutomaticPayoutRetryAction(invoiceId, {
          retryAutomaticPayout,
          writeNote: (targetInvoiceId, note) =>
            updateInvoiceNotes({ invoiceID: targetInvoiceId, note })
        });
        // openDialog() refreshes this record, so the button disappears once
        // the API no longer reports the payment as retry-eligible.
        if (this.payInts[0]?.id === invoiceId) {
          this.openDialog(outcome.message, outcome.requiresAction);
        }
      } finally {
        this.paymentActionPending = false;
      }
    },
    async addTicketLink(): Promise<void> {
      try {
        await updateInvoice({
          invoiceId: this.payInts[0].id,
          ticketLinks: this.note
        });
        this.openDialog("The Ticket Link was updated");
      } catch (error) {
        this.openDialog("The Ticket Link wasn't updated", true);
      }
    },
    async sendHygAsstResponse(): Promise<void> {
      try {
        const mailTheme =
          this.payInts[0].dueYet < 0
            ? "Hyg Payment Response Overdue"
            : "Hyg Payment Response Not Due";
        const noteDue = this.payInts[0].dueYet < 0 ? "overdue" : "not due";
        const dateNow = format(new Date(), "yyyy-MM-dd HH:mm:ss");

        await Promise.all([
          sendHygAsstResponse({
            mailTheme,
            email: this.payInts[0].email,
            invoiceID: this.payInts[0].id,
            firstName: this.payInts[0].firstName,
            lastName: this.payInts[0].lastName,
            shiftDate: this.payInts[0].startDate,
            amount: this.payInts[0].totalInvoiceAmt,
            paymentDue: this.payInts[0].paymentDue
          }),
          updateInvoiceNotes({
            invoiceID: this.payInts[0].id,
            note: `${noteDue} follow-up e-mail sent`
          }),
          updateInvoice({
            invoiceId: this.payInts[0].id,
            unresolved: dateNow
          })
        ]);

        this.openDialog("Hyg/Asst Follow-Up Sent");
      } catch (error) {
        this.openDialog("Hyg/Asst Follow-Up didn't send", true);
      }
    },
    async addLogging(btnName: string): Promise<void> {
      await this.addAdminLogging({
        adminId: parseInt(localStorage.getItem("adminId")!),
        buttonUsed: btnName,
        hygienistId: this.payInts[0].hygienistId,
        firstName: this.payInts[0].firstName,
        lastName: this.payInts[0].lastName,
        locationId: this.payInts[0].locationId,
        practiceName: this.payInts[0].practiceName,
        email: this.payInts[0].email
      });
    },
    async saveNotes(): Promise<void> {
      await updateTable({
        tablename: this.modalNotesTitle,
        columnname: this.modalNotesTitle === "Hygienist" ? "Notes" : "notes",
        id:
          this.modalNotesTitle === "Invoice"
            ? this.payInts[0].id
            : this.payInts[0][`${this.modalNotesTitle.toLowerCase()}Id`],
        value: this.modalNotes,
        prevValue: this.payInts[0][`${this.modalNotesTitle.toLowerCase()}Notes`]
      });
      this.dialogNotes = false;
      this.updateTable();
    },
    namePayNowUsed(status: number) {
      switch (status) {
        case 0:
          return "Not Paid Yet";
        case 1:
          return "Pay Now";
        case 2:
          return "Other Payment type";
        default:
          break;
      }
    }
  }
});
